// src/components/chat/LastMessagePreview.jsx
import React from 'react';
import { getMessageStatus } from '../../utils/messageHelpers';
import { extractUrls } from '../../utils/fileHelpers';
import { truncateFileName } from '../../utils/fileHelpers';
import { hasUrl} from '../../utils/urlUtils';
import './LastMessagePreview.css';

const LastMessagePreview = ({ chat }) => {
    const isGroup = chat.type === 'group';
    const typingName=chat.display_name || 'rnj-nj'
    if (chat.is_typing) {
        return (
            <div className="chat-last-message">
                <span className="last-message-text typing-text">
                     {isGroup && <b>{typingName} </b>}
                     <span className="typing-label">
                        {['п', 'е', 'ч', 'а', 'т', 'а', 'е', 'т'].map((char, i) => (
                            <span key={i}>{char}</span>
                        ))}
                    </span>
                </span>
            </div>
        );
    }
    const msg = chat.last_message;
    if (!msg) return <span className="last-message-text">Нет сообщений</span>;

    if (msg.is_deleted) {
        return (
            <div className="chat-last-message">
                <i className='bi bi-trash'></i>
                <span className="last-message-text deleted-message">Сообщение удалено</span>
            </div>
        );
    }

    
    const isOutgoing = msg.is_outgoing === true;

    let statusIcon = '';
    let statusColor = '#999999';
    if (isOutgoing) {
        const status = getMessageStatus(msg);
        statusIcon = status.icon;
        statusColor = status.color;
    }

    // ✅ Текст сообщения с учетом типа
    let messageText = '';
    const hasCaption = msg.content && msg.content.trim() !== '';
    const captionText = hasCaption ? `: ${msg.content}` : '';
    
    if (msg.type === 'text') {
        if (hasUrl(msg.content)) {
            // Ссылка есть — показываем [ссылка] или обрезаем
            const urls = extractUrls(msg.content);
            const firstUrl = urls[0];
            
            // Если весь текст — это ссылка
            if (msg.content.trim() === firstUrl) {
                messageText = '🔗 Ссылка';
            } else {
                // Текст содержит ссылку, но есть и другой текст
                // Обрезаем ссылку и показываем текст без неё
                let textWithoutUrl = msg.content;
                urls.forEach(url => {
                    textWithoutUrl = textWithoutUrl.replace(url, '');
                });
                // Показываем первые 30 символов текста без ссылки
                const previewText = textWithoutUrl.trim().substring(0, 30);
                messageText = previewText ? previewText + '...' : '🔗 Ссылка';
            }
        } else {
           messageText = msg.content || ''; 
        }
        
    } else if (msg.type === 'sticker') {
        messageText = '🎨 Стикер';
    } else if (msg.type === 'media') {
        // ✅ Получаем файлы из разных источников
        let files = [];
        
        // 1. Из attachments (WebSocket)
        if (msg.attachments && msg.attachments.length > 0) {
            files = msg.attachments;
        }
        // 2. Из media_url (сервер)
        else if (msg.media_url) {
            files = [{ 
                url: msg.media_url,
                name: msg.file_name || 'Файл',
                type: msg.file_type || msg.mime_type || 'application/octet-stream'
            }];
        }
        // 3. Из files (альтернативный формат)
        else if (msg.files && msg.files.length > 0) {
            files = msg.files;
        }
        // 4. Если есть content и нет файлов — просто текст
        else if (hasCaption) {           
            messageText = msg.content;
        }
        
        
        if (files.length === 0 && !hasCaption) {
            messageText = '📎 Медиа';
        } else if (files.length === 0 && hasCaption) {
            messageText = `📎 Медиа: ${msg.content}`;
        } else if (files.length === 1) {
            const file = files[0];
            const fileType = file.type || file.mime_type || file.file_type;
            const fileName = file.name || file.file_name || 'Файл';
            
            // Определяем тип файла
            let icon = '📎';
            let label = 'Файл';
            
            if (fileType.startsWith('image') || fileName.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)) {
                icon = <i className="bi bi-image"></i>;
                label = 'Фото';
            } else if (fileType.startsWith('video') || fileName.match(/\.(mp4|mov|avi|mkv)$/i)) {
                icon = <i className="bi bi-film"></i>;
                label = 'Видео';
            } else if (fileType.startsWith('audio') && fileName.match(/\.(mp3|wav|ogg|flac|m4a)$/i)) {
                icon = <i className="bi bi-music-note-beamed"></i>;
                label = 'Аудио';
            } else if (fileName.match(/\.webm$/i)) {
                icon = <i className="bi bi-mic"></i>;
                label = 'Голосовое';
            } else if (fileType.includes('pdf') || fileName.match(/\.pdf$/i)) {
                icon = <i className="bi bi-file-pdf"></i>;
                label = 'PDF';
            } else if (fileType.includes('word') || fileName.match(/\.(doc|docx|rtf)$/i)) {
                icon = <i className="bi bi-file-word"></i>;
                label = 'Документ';
            } else if (fileType.includes('excel') || fileName.match(/\.(xls|xlsx)$/i)) {
                icon = <i className="bi bi-file-excel"></i>;
                label = 'Таблица';
            } else if (fileName.match(/\.(zip|rar|7z|tar|gz)$/i)) {
                icon = <i className="bi bi-file-zip"></i>;
                label = 'Архив';
            } else {
                // Показываем имя файла
                label = truncateFileName(fileName);
            }          
          
            messageText=(<>{icon} {label} {captionText}</>)
        } else if (files.length > 1 && files.length <= 3 ) {
            // Несколько файлов
            const imageCount = files.filter(f => {
                const type = f.type || f.file_type || '';
                const name = f.name || f.file_name || '';
                return type.startsWith('image') || name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
            }).length;
            
            const videoCount = files.filter(f => {
                const type = f.type || f.file_type || '';
                const name = f.name || f.file_name || '';
                return type.startsWith('video') || name.match(/\.(mp4|webm|mov|avi)$/i);
            }).length;
            
            const audioCount = files.filter(f => {
                const type = f.type || f.file_type || '';
                const name = f.name || f.file_name || '';
                return type.startsWith('audio') || name.match(/\.(mp3|wav|ogg|flac)$/i);
            }).length;

            const voiceCount = files.filter(f => {
                const type = f.type || f.file_type || '';
                const name = f.name || f.file_name || '';
                return type.startsWith('audio/webm') || name.match(/\.webm$/i);
            }).length;

            const wordCount = files.filter(f => {
                const type = f.type || f.file_type || '';
                const name = f.name || f.file_name || '';
                return type.startsWith('word') || name.match(/\.(doc|docx|rtf)$/i);
            }).length;

             const excelCount = files.filter(f => {
                const type = f.type || f.file_type || '';
                const name = f.name || f.file_name || '';
                return type.startsWith('excel') || name.match(/\.(xls|xlsx)$/i);
            }).length;
            const pdfCount = files.filter(f => {
                const type = f.type || f.file_type || '';
                const name = f.name || f.file_name || '';
                return type.startsWith('pdf') || name.match(/\.pdf$/i);
            }).length;
            
            const otherCount = files.length - imageCount - videoCount - audioCount-voiceCount-wordCount - excelCount-pdfCount;
            
            const parts = [];
            if (imageCount > 0) parts.push(`🖼️ ${imageCount} фото`);
            if (videoCount > 0) parts.push(`🎬 ${videoCount} видео`);
            if (audioCount > 0) parts.push(`🎵 ${audioCount} аудио`);
            if (voiceCount > 0) parts.push(`🎤 ${voiceCount} голосовых`);
            if (wordCount > 0) parts.push(`📄 ${wordCount} док.`);
            if (excelCount > 0) parts.push(`📊 ${excelCount} табл.`);
            if (pdfCount > 0) parts.push(`📙 ${pdfCount} PDF`);
            if (otherCount > 0) parts.push(`📎 ${otherCount} файл(ов)`);
            
            messageText = parts.join(' ') + captionText;
        } else { messageText = (<><i className="bi bi-files"></i> {files.length} Файлов</>)}
    } else {
        // Другие типы сообщений
        messageText = msg.content || 'Сообщение';
        if (msg.is_forwarded) {
            messageText = 'Пересланное сообщение';
        }
    }
    
    // Для групповых чатов добавляем имя отправителя
    if (isGroup) {
        if(!isOutgoing) {
             const senderName = msg.display_name || msg.from_username || `User ${msg.from_id}`;
        messageText =(<><i>{senderName}:</i> {messageText}</>)
        } else {
            messageText=(<><i>Вы: </i> {messageText}</>)
        }
     
    }
    if (msg.is_forwarded) {
        messageText = (
            <>
                <i className="bi bi-forward" style={{ fontSize: '1.2em', marginRight: '2px' }}></i>
                {messageText !=='' ? messageText: 'Пересланое сообщение'} 
            </>
        );
    }

    return (
        <div className="chat-last-message">
            <span className="last-message-text">{messageText}</span>
            {statusIcon && <span className="last-message-status" style={{ color: statusColor }}>{statusIcon}</span>}
            {!isOutgoing && chat.unread_count > 0 && (
                <div className="unread-badge">{chat.unread_count > 99 ? '99+' : chat.unread_count}</div>
            )}
        </div>
    );
};

export default LastMessagePreview;